import { useContext, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { ScrollerContext, prefersReducedMotion } from "../../RoyalMahal/hooks";
import { heroIllustration } from "../helpers";

const clamp = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
// Gentle at both ends, so the couple leaves the courtyard and lands in their place softly
const ease = (t) => t * t * (3 - 2 * t);
// A little extra zoom at the middle of the flight
const ZOOM = 0.08;
// The illustration's shape until it has loaded (width / height)
const DEFAULT_RATIO = 0.704;

// An element's box in the scroller's content coordinates (unaffected by scrolling)
const boxIn = (scroller, el) => {
  const r = el.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  return { x: r.left - s.left + scroller.scrollLeft, y: r.top - s.top + scroller.scrollTop, w: r.width, h: r.height };
};
// Where the picture is drawn inside a box: contained, centred, standing on its foot
const drawnIn = (b, ratio) => {
  const w = Math.min(b.w, b.h * ratio);
  const h = w / ratio;
  return { x: b.x + (b.w - w) / 2, y: b.y + b.h - h, w, h };
};
const ratioOf = (img) => (img?.naturalWidth && img?.naturalHeight ? img.naturalWidth / img.naturalHeight : DEFAULT_RATIO);
// The flight ends when the couple's place reaches the middle of the screen
const landingFor = (b, vh) => b.y + b.h / 2 - vh / 2;

/**
 * The scroll position at which the couple has landed in their place on the couple page,
 * i.e. where the flight ends (null before the page is there). The "Scroll down" prompt
 * glides here (scenes/Hero.js).
 */
export const coupleLanding = (scroller) => {
  const to = scroller?.querySelector(".ut-couple__figure");
  if (!to) return null;
  const b = drawnIn(boxIn(scroller, to), ratioOf(scroller.querySelector(".ut-flight")));
  return Math.max(0, Math.round(landingFor(b, scroller.clientHeight)));
};

/**
 * The couple's illustration, flying from the courtyard arch on the first page (Hero.js)
 * to their place at the top of the couple page (Couple.js) as the guest scrolls between
 * them. It is driven by the scroll position, frame by frame, so scrolling back up takes
 * them home again. As they leave, the courtyard behind them zooms in a little (`--zoom`
 * on the first page, 0 to 1; styles/_hero.scss), staying on its own page.
 *
 * The two pages keep an invisible copy of the illustration in each place: they hold the
 * layout and mark where the flight starts and ends. This one image is positioned in the
 * scroller's own coordinates, so at rest it scrolls with the page like the copies it
 * stands in for. While it flies, `data-flight` on the scroller hides those copies
 * (styles/_sections.scss). With reduced motion it stays put and the copies show instead.
 */
const CoupleFlight = ({ invite }) => {
  const src = heroIllustration(invite);
  const scrollerRef = useContext(ScrollerContext);
  const ref = useRef(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const img = ref.current;
    if (!scroller || !img || !src || prefersReducedMotion()) return undefined;
    const from = scroller.querySelector(".ut-hero__illustration");
    const to = scroller.querySelector(".ut-couple__figure");
    const stage = scroller.querySelector(".ut-couple__stage");
    const hero = from?.closest("section");
    if (!from || !to || !stage || !hero) return undefined;

    scroller.dataset.flight = "";
    let frame = 0;
    let size = "";

    const update = () => {
      frame = 0;
      const ratio = ratioOf(img);
      const a = drawnIn(boxIn(scroller, from), ratio);
      const b = drawnIn(boxIn(scroller, to), ratio);
      const heroBox = boxIn(scroller, hero);
      const vh = scroller.clientHeight;
      const top = scroller.scrollTop;

      // Lifts off once the first page starts to leave; lands as its place reaches the middle of the screen
      const start = Math.max(0, heroBox.y + heroBox.h - vh);
      const end = Math.max(start + 1, landingFor(b, vh));
      const p = clamp((top - start) / (end - start));
      const t = ease(p);

      const w = lerp(a.w, b.w, t) * (1 + ZOOM * Math.sin(Math.PI * p));
      const h = w / ratio;
      // Keep the feet on the line between the two places, so the zoom grows upward and out
      const x = lerp(a.x + a.w / 2, b.x + b.w / 2, t) - w / 2;
      const y = lerp(a.y + a.h, b.y + b.h, t) - h;

      const next = `${b.w.toFixed(1)}px`;
      if (next !== size) {
        size = next;
        img.style.width = next;
      }
      img.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${(w / b.w).toFixed(4)})`;
      stage.style.setProperty("--p", t.toFixed(4));
      // The zoom answers the first touch of the scroll and eases out as the page leaves
      hero.style.setProperty("--zoom", Math.sin((p * Math.PI) / 2).toFixed(4));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    img.addEventListener("load", schedule);
    scroller.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Fonts and photos arriving move things about without any scrolling
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    observer?.observe(hero);
    observer?.observe(stage.closest("section") || stage);

    return () => {
      cancelAnimationFrame(frame);
      img.removeEventListener("load", schedule);
      scroller.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      delete scroller.dataset.flight;
      stage.style.removeProperty("--p");
      hero.style.removeProperty("--zoom");
    };
  }, [scrollerRef, src]);

  if (!src) return null;
  return <img ref={ref} className="ut-flight" src={src} alt="" aria-hidden="true" decoding="async" />;
};

const mapStateToProps = (state) => ({
  invite: state.inviteDetails.invite,
});

export default connect(mapStateToProps)(CoupleFlight);
